// @ts-check

/** @param {{ spark: number[], partialLast?: boolean }} props */
function Sparkline({ spark, partialLast = false }) {
  if (!spark || spark.length < 2) return null;
  const max = Math.max(...spark, 1);
  const w = 100;
  const h = 24;
  const pts = spark.map((v, i) => `${(i / (spark.length - 1)) * w},${h - (v / max) * (h - 2) - 1}`);
  // The last point is today, still in progress: draw its segment dashed so it is not read as a drop.
  const solid = partialLast && pts.length > 2 ? pts.slice(0, -1) : pts;
  const dashed = partialLast && pts.length > 2 ? pts.slice(-2) : null;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-6 mt-3 text-muted-foreground" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={solid.join(' ')} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      {dashed && (
        <polyline
          points={dashed.join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
          data-testid="spark-partial"
        />
      )}
    </svg>
  );
}

/**
 * Compact KPI card: value, change vs the previous period (em dash when there is nothing to compare), sparkline.
 * @param {{ label: string, caption?: string, value: number, deltaPct: number | null, spark?: number[], partialLast?: boolean }} props
 */
export default function StatCard({ label, caption, value, deltaPct, spark = [], partialLast = false }) {
  const hasDelta = deltaPct !== null && deltaPct !== undefined;
  const rounded = hasDelta ? Math.round(deltaPct) : 0;
  const tone = !hasDelta || rounded === 0 ? 'text-muted-foreground' : rounded > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  const text = !hasDelta ? '—' : `${rounded > 0 ? '+' : ''}${rounded}%`;
  return (
    <div data-testid="stat-card" className="bg-card p-4 border rounded-lg border-border shadow-sm">
      <h3 className="text-sm font-medium text-muted-foreground">
        {label}
        {caption && <span className="ml-1 text-xs font-normal">({caption})</span>}
      </h3>
      <p className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-bold">{Number(value || 0).toLocaleString('en-IN')}</span>
        <span data-testid="stat-delta" className={`text-sm ${tone}`}>
          {text}
        </span>
      </p>
      <Sparkline spark={spark} partialLast={partialLast} />
    </div>
  );
}
